import { getConfigByType } from '@editor/components/UpgradeFlowModal/config';
import analytics from '@repo/analytics';
import { getWorkspaceMe } from '@repo/api-client';
import { create } from 'zustand';

import { ImageGenerationModel } from '../components/AiGenerators/types';
import {
  ImageComponentType,
  UpgradeModalContentType,
  upgradeModalContent,
} from '../components/UpgradeFlowModal/types';
import {
  UPGRADE_MODAL_CLICK,
  UPGRADE_MODAL_SHOW,
} from '../global/analytics/events';
import { cleanQueryParams } from '../utils/url';
import { urlParams } from '../utils/window';

import { useUserStore } from './userStore';

export const SUCCESS_UPGRADE_PAYMENT_URL_PARAM = 'successUpgradePayment';

/** @knipignore - used in website */
export const SUCCESS_UPGRADE_PAYMENT_URL_PARAM_VALUE = 't';
const REDIRECT_STATUS_URL_PARAM = 'redirect_status';
const REDIRECT_STATUS_FAILED = 'failed';
export const IS_ONLY_EXPERT_UPGRADE_PARAM = 'onlyExpert';

const upgradeImageByType: {
  [key in UpgradeModalContentType]?: ImageComponentType;
} = {
  premiumDesignUsage: 'designPreview',
  backgroundReplacer: 'backgroundReplacer',
  upgradeToPaidPlan: 'upgradeToPaidPlan',
  logoGenUpgrade: 'logoGenUpgrade',
  designGenUpgrade: 'designGenUpgrade',
  premiumCopilotElementsUsage: 'premiumElements',
  premiumCopilotTemplatesUsage: 'copilotTemplates',
  upgradePlanWithMoreCreditsAsFreeUserAiPanel: 'aiImages',
  upgradePlanAsFreeUserAiPanel: 'aiImages',
  aiSaveStyleUpgradeToExpert: 'aiSaveStyleUpgradeToExpert',
  eraseFill: 'eraseFill',
};

const getUpgradeImage = (type: UpgradeModalContentType): ImageComponentType => {
  return upgradeImageByType[type] ?? 'brands';
};

interface ShowUpgradeOptionsType {
  designPreview?: string;
  data?: { [key: string]: string | number };
  returnUrlParamValue?: string;
  textToImageModelVersion?: ImageGenerationModel;
  /**
   * The label to use for tracking instead of the upgrade modal type. This is useful when the modal content type is the noIncentiveUpgrade type and in other cases where the type is not fit for tracking purpose.
   */
  trackingLabel?: string;
  onClickCallback?: () => Promise<void>;
}

interface State {
  upgradeModalIsOpen: boolean;
  upgradeModalType: UpgradeModalContentType | null;
  upgradeModalImageComponent: ImageComponentType;
  upgradeModalDesignPreview: string;
  upgradeSuccessModalIsOpen: boolean;
  upgradeOnClickCallback: (() => Promise<void>) | undefined;
  returnUrlFailed: boolean;
  returnUrlParams: { [key: string]: string };
  upgradeCallbacks: { [key: string]: () => void };
  isOnlyExpertUpgrade?: boolean;

  init: () => void;
  onSuccess: () => void;
  closeUpgradeModal: () => void;
  showUpgradeModal: (
    type: UpgradeModalContentType,
    options?: ShowUpgradeOptionsType,
    onClickCallback?: () => Promise<void>
  ) => void;
  subscribeToSuccessUpgrade: (
    returnUrlParam: string,
    callback: () => void
  ) => void;
  unsubscribeToSuccessUpgrade: (returnUrlParam: string) => void;
  resetReturnUrlFailed: () => void;
}

/**
 * a store to manage and show promotions, like the upgrade account modal or
 * the invite friends popup
 */
export const usePromotionStore = create<State>((set, get) => ({
  /**
   * whether the upgrade modal is open or not
   * @type Boolean
   */
  upgradeModalIsOpen: false,

  /**
   * the type of the current upgrade modal
   * @type string | null
   */
  upgradeModalType: null,

  /**
   * image component used by the upgradeModal
   * @type string
   */
  upgradeModalImageComponent: 'brands',

  /**
   * a preview that can be displayed in the UpgradeModal of type `premiumDesignUsage`
   * @type string | null
   */
  upgradeModalDesignPreview: '',

  /**
   * callback that is called when the user successfully upgraded and returned from the external payment page
   */
  upgradeSuccessModalIsOpen: false,

  /**
   * custom callback that is called when the user clicks the upgrade button
   */
  upgradeOnClickCallback: undefined,

  returnUrlParams: {},

  upgradeCallbacks: {},

  returnUrlFailed: false,
  /**
   * Render upgrade modal with only expert plan available
   */
  isOnlyExpertUpgrade: false,

  onSuccess: (): void => undefined,

  init: (): void => {
    const returnUrlParam = urlParams.get(SUCCESS_UPGRADE_PAYMENT_URL_PARAM);
    const redirectStatusUrlParam = urlParams.get(REDIRECT_STATUS_URL_PARAM);
    const isOnlyExpertPlanParam = urlParams.get(IS_ONLY_EXPERT_UPGRADE_PARAM);

    cleanQueryParams(urlParams, [
      SUCCESS_UPGRADE_PAYMENT_URL_PARAM,
      REDIRECT_STATUS_URL_PARAM,
      IS_ONLY_EXPERT_UPGRADE_PARAM,
    ]);
    if (returnUrlParam) {
      set({
        upgradeSuccessModalIsOpen: true,
        upgradeModalType: null,
        onSuccess: () => {
          const { unsubscribeToSuccessUpgrade, upgradeCallbacks } = get();
          upgradeCallbacks[returnUrlParam]?.();
          unsubscribeToSuccessUpgrade(returnUrlParam);
          set({ upgradeSuccessModalIsOpen: false });
        },
      });
    }
    if (redirectStatusUrlParam === REDIRECT_STATUS_FAILED) {
      set({
        upgradeModalType: 'noIncentiveUpgrade',
        upgradeModalIsOpen: true,
        returnUrlFailed: true,
        isOnlyExpertUpgrade: isOnlyExpertPlanParam === 'true',
      });
    }
  },
  resetReturnUrlFailed: (): void => {
    set({ returnUrlFailed: false });
  },

  /**
   * trigger showing the upgrade modal for a type
   * @param {String} type - type of the modal to show
   * @param {Object} options - add more options that are only used for certain types or in certain scenarios
   * @param {String} options.designPreview - previewImage to be displayed in premiumDesignUsage
   * @param {String} options.data - extra data to track with the event
   * @param {String} options.returnUrlParamValue - param to identify the callback
   */
  showUpgradeModal: async (
    type: UpgradeModalContentType,
    options?: ShowUpgradeOptionsType,
    onClickCallback?: () => Promise<void>
  ): Promise<void> => {
    if (get().upgradeModalIsOpen && get().upgradeModalType === type) {
      return;
    }
    if (!upgradeModalContent.includes(type)) {
      console.warn(`Unknown upgrade modal type: ${type}`);
      return;
    }

    const user = useUserStore.getState().user;
    const ws = await getWorkspaceMe();
    if (!ws.success) {
      console.warn('No workspace found');
      return;
    }
    const plan = ws.workspace.plan;

    const {
      data,
      designPreview,
      returnUrlParamValue,
      textToImageModelVersion,
      trackingLabel,
    } = options || {};

    if (!!user && (!user?.team || user.team.role === 'ADMIN')) {
      // only track events if we show the real upgrade modal
      analytics.track(UPGRADE_MODAL_SHOW, {
        ...data,
        label: trackingLabel || type,
        plan,
        ...(textToImageModelVersion ? { textToImageModelVersion } : {}),
      });

      if (type === 'noIncentiveUpgrade') {
        analytics.track(UPGRADE_MODAL_CLICK, {
          label: trackingLabel || type,
        });
      }
    }
    const { onlyExpert } = getConfigByType(type);

    const returnUrlParams: Record<string, string> = {
      [SUCCESS_UPGRADE_PAYMENT_URL_PARAM]:
        returnUrlParamValue ?? SUCCESS_UPGRADE_PAYMENT_URL_PARAM_VALUE,
    };

    if (onlyExpert) {
      returnUrlParams[IS_ONLY_EXPERT_UPGRADE_PARAM] = 'true';
    }

    set({
      upgradeModalIsOpen: true,
      upgradeModalType: type,
      upgradeModalImageComponent: getUpgradeImage(type),
      upgradeModalDesignPreview: designPreview,
      upgradeOnClickCallback: onClickCallback ?? undefined,
      returnUrlParams,

      onSuccess: returnUrlParamValue
        ? (): void => {
            const { unsubscribeToSuccessUpgrade, upgradeCallbacks } = get();
            upgradeCallbacks[returnUrlParamValue]?.();
            unsubscribeToSuccessUpgrade(returnUrlParamValue);
          }
        : undefined,
    });
  },

  closeUpgradeModal: (): void => {
    set({
      upgradeModalIsOpen: false,
      upgradeSuccessModalIsOpen: false,
      upgradeCallbacks: {},
      upgradeOnClickCallback: undefined,
    });
  },

  subscribeToSuccessUpgrade: (
    returnUrlParam: string,
    callback: () => void
  ): void => {
    set({
      upgradeCallbacks: {
        ...get().upgradeCallbacks,
        [returnUrlParam]: callback,
      },
    });
  },

  unsubscribeToSuccessUpgrade: (returnUrlParam: string): void => {
    set({
      upgradeCallbacks: Object.fromEntries(
        Object.entries(get().upgradeCallbacks).filter(
          ([key]) => key !== returnUrlParam
        )
      ),
    });
  },
}));

export const promotionSelector = {
  upgradeModalProps: (
    state: State
  ): {
    isOpen: State['upgradeModalIsOpen'];
    upgradeSuccessModalIsOpen: State['upgradeSuccessModalIsOpen'];
    contentType: State['upgradeModalType'];
    designPreview: State['upgradeModalDesignPreview'];
    imageComponentType: State['upgradeModalImageComponent'];
    returnUrlParams: State['returnUrlParams'];
    returnUrlFailed: State['returnUrlFailed'];
    isOnlyExpertUpgrade: State['isOnlyExpertUpgrade'];
    onClose: State['closeUpgradeModal'];
    onSuccess: State['onSuccess'];
    resetReturnUrlFailed: State['resetReturnUrlFailed'];
    onClickCallback: State['upgradeOnClickCallback'];
  } => ({
    isOpen: state.upgradeModalIsOpen,
    upgradeSuccessModalIsOpen: state.upgradeSuccessModalIsOpen,
    contentType: state.upgradeModalType,
    designPreview: state.upgradeModalDesignPreview,
    imageComponentType: state.upgradeModalImageComponent,
    returnUrlParams: state.returnUrlParams,
    returnUrlFailed: state.returnUrlFailed,
    isOnlyExpertUpgrade: state.isOnlyExpertUpgrade,
    onClose: state.closeUpgradeModal,
    onSuccess: state.onSuccess,
    resetReturnUrlFailed: state.resetReturnUrlFailed,
    onClickCallback: state.upgradeOnClickCallback,
  }),
  init: (state: State): State['init'] => state.init,
  showUpgradeModal: (state: State): State['showUpgradeModal'] =>
    state.showUpgradeModal,
  closeUpgradeModal: (state: State): State['closeUpgradeModal'] =>
    state.closeUpgradeModal,
  upgradeModalIsOpen: (state: State): State['upgradeModalIsOpen'] =>
    state.upgradeModalIsOpen,
  upgradeSuccessModalIsOpen: (
    state: State
  ): State['upgradeSuccessModalIsOpen'] => state.upgradeSuccessModalIsOpen,

  subscribeToSuccessUpgrade: (
    state: State
  ): State['subscribeToSuccessUpgrade'] => state.subscribeToSuccessUpgrade,
  unsubscribeToSuccessUpgrade: (
    state: State
  ): State['unsubscribeToSuccessUpgrade'] => state.unsubscribeToSuccessUpgrade,
};
